import { CircleHelp } from "lucide-react";
import { Button, CloseButton, Dialog } from "../ui/index.js";
import { OnboardingIllustration } from "./illustrations.js";
import { CHAPTERS } from "./onboarding-steps.js";
import type { OnboardingController } from "./useOnboarding.js";
import "./onboarding.css";

const statusLabel = { done: "erledigt", skipped: "übersprungen" } as const;

/** Das Tour-Modal: ein Schritt pro Ansicht, Fortschritt als Punkte darunter. */
export function OnboardingDialog({ tour }: { tour: OnboardingController }) {
  const { current, steps, index, summary } = tour;
  if (!tour.open || !current) return null;
  const chapterTitle =
    CHAPTERS.find((chapter) => chapter.id === tour.chapter)?.title ?? "";
  const last = index === steps.length - 1;
  return (
    <Dialog
      open
      onClose={tour.close}
      title={`Tour: ${chapterTitle}`}
      contentClassName="ui-modal-card onboarding-dialog"
    >
      <header>
        <h2>
          <CircleHelp aria-hidden="true" /> {chapterTitle.toUpperCase()} · TOUR
        </h2>
        <CloseButton onClick={tour.close} aria-label="Tour schließen" />
      </header>
      {tour.manual ? (
        <nav className="onboarding-chapters" aria-label="Kapitel">
          {CHAPTERS.map((chapter) => {
            const counts = tour.chapterSummary(chapter.id);
            return (
              <button
                key={chapter.id}
                type="button"
                aria-pressed={chapter.id === tour.chapter}
                onClick={() => tour.selectChapter(chapter.id)}
              >
                {chapter.title}
                <span>
                  {counts.done + counts.skipped}/{counts.total}
                </span>
              </button>
            );
          })}
        </nav>
      ) : null}
      <div className="onboarding-figure" aria-hidden="true">
        <OnboardingIllustration name={current.illustration} />
      </div>
      <p className="onboarding-meta" aria-live="polite">
        Schritt {index + 1} von {steps.length} · {summary.done} erledigt ·{" "}
        {summary.skipped} übersprungen
      </p>
      <h3 className="onboarding-title">{current.title}</h3>
      <p className="onboarding-body">{current.body}</p>
      <ol className="onboarding-dots" aria-label="Schritte">
        {steps.map((step, stepIndex) => {
          const status = tour.progress[step.id];
          return (
            <li key={step.id}>
              <button
                type="button"
                className={`onboarding-dot${status ? ` is-${status}` : ""}`}
                aria-current={stepIndex === index ? "step" : undefined}
                aria-label={`Schritt ${stepIndex + 1}: ${step.title}${status ? ` (${statusLabel[status]})` : ""}`}
                onClick={() => tour.goTo(stepIndex)}
              />
            </li>
          );
        })}
      </ol>
      <footer className="onboarding-actions">
        <Button onClick={tour.skipRest} disabled={summary.pending === 0}>
          Tour beenden
        </Button>
        <span className="onboarding-actions-spacer" />
        <Button onClick={tour.back} disabled={index === 0}>
          Zurück
        </Button>
        <Button onClick={tour.skipStep}>Überspringen</Button>
        {/* Radix fokussiert sonst das Schließen-X; der Hauptweg ist „Weiter“. */}
        <Button variant="primary" onClick={tour.next} autoFocus>
          {last ? "Fertig" : "Weiter"}
        </Button>
      </footer>
    </Dialog>
  );
}
